import { AppBar, Theme, Toolbar, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import packageJSON from '../../../package.json';
import { useAppSelector } from '../../app/hooks';
import { MenuStages, Routes } from '../../constants/enums';
import {
  selectFeatureFlagMenuVersion,
  selectFeatureFlagTaskRouter,
} from '../../redux/features/config/config.selector';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { AgentStatuses } from '../../redux/features/taskRouter/taskRouter.constants';
import { selectCurrentTask } from '../../redux/features/taskRouter/taskRouter.selector';
import { selectLoadedVersionDetails } from '../../selectors/menu';
import {
  selectActiveRestaurantDetails,
  selectedStageSelector,
} from '../../selectors/restaurant';
import { RolePermissions } from '../../types/restaurant';
import {
  hasRoleAccess,
  selectedRestaurantAccessSelector,
} from '../../utils/restaurants';
import HeaderIndicators from '../HeaderIndicators';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Logout from '../Login/Logout';
import MenuVersionPicker from '../MenuVersionPicker';
import MenuVersionStage from '../MenuVersionStage';
import RestaurantsContainer from '../RestaurantsContainer';
import TaskRouterHeaderInfo from '../TaskRouterHeaderInfo';
import logger from '../../utils/logger';
import { copyTextToClipboard } from '../../utils';

type HeaderProps = {
  isTaskRouterPage?: boolean;
  handleAgentAvailability: (agentStatus: AgentStatuses) => void;
  sendAgentEvent?: Function;
  logout: Function;
  sendHealthScore?: Function;
};

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    paddingLeft: theme.spacing(2),
    flexGrow: 1,
  },
  restaurantPicker: {
    marginLeft: theme.spacing(2),
  },
  menuVersionPicker: {
    marginLeft: theme.spacing(2),
  },
  logout: {
    marginLeft: theme.spacing(2),
  },
  copyButton: {
    '&:hover': {
      backgroundColor: `rgba(244, 244, 244, 0.5) !important`,
    },
  },
}));

const Header: FC<HeaderProps> = ({
  handleAgentAvailability,
  sendAgentEvent,
  logout,
  sendHealthScore,
}) => {
  const location = useLocation();
  const taskRouterFeatureFlag = useAppSelector(selectFeatureFlagTaskRouter);
  const currentTaskId = useAppSelector(selectCurrentTask);

  const isTaskRouterPage =
    taskRouterFeatureFlag && location.pathname.indexOf(Routes.taskRouter) >= 0;

  const classes = useStyles();
  const title = `Voice Terminal v${packageJSON.version}`;
  const { restaurantCode, primaryRestaurantCode } = useSelector(
    selectActiveRestaurantDetails
  );
  const selectedStage = useSelector(selectedStageSelector);
  const sessionId = useAppSelector(selectSessionId) || '';
  const selectedRestaurantAccessLevel = useSelector(
    selectedRestaurantAccessSelector
  );
  const hasMenuVersionAccess = hasRoleAccess(
    RolePermissions.RestaurantManager,
    selectedRestaurantAccessLevel
  );
  const FEATURE_FLAG_MENU_VERSION = useSelector(selectFeatureFlagMenuVersion);
  const loadedVersionDetails = useSelector(selectLoadedVersionDetails);
  const { isBrandMenu, menuVersion } = loadedVersionDetails || {};

  const canChangeMenuVersion =
    FEATURE_FLAG_MENU_VERSION &&
    hasMenuVersionAccess &&
    restaurantCode &&
    !currentTaskId &&
    !primaryRestaurantCode;

  return (
    <AppBar position="static" data-header="true">
      <Toolbar>
        <img src="/presto_logo.svg" alt="Presto Logo" />
        <div className={classes.title}>
          <Typography>{title}</Typography>
          {!isTaskRouterPage && restaurantCode && sessionId ? (
            <Typography>
              {sessionId}&nbsp;
              <IconButton
                className={classes.copyButton}
                onClick={() => copyTextToClipboard(sessionId, logger)}
              >
                <ContentCopyIcon fontSize="small" style={{ color: 'white' }} />
              </IconButton>
            </Typography>
          ) : null}
        </div>
        {!isTaskRouterPage && (
          <>
            <HeaderIndicators />
            {canChangeMenuVersion && (
              <div className={classes.restaurantPicker}>
                <MenuVersionStage logout={logout} />
              </div>
            )}
            <div className={classes.restaurantPicker}>
              <RestaurantsContainer
                logout={logout}
                sendHealthScore={sendHealthScore}
              />
            </div>
            <div className={classes.restaurantPicker}>
              {canChangeMenuVersion &&
              selectedStage === MenuStages.PLAYGROUND ? (
                <MenuVersionPicker logout={logout} />
              ) : (
                <>
                  {menuVersion ? (
                    <span>{`${
                      isBrandMenu ? 'BM' : 'PRP'
                    }: ${menuVersion}`}</span>
                  ) : null}
                </>
              )}
            </div>
          </>
        )}
        <div className={classes.logout}>
          <Logout sendAgentEvent={sendAgentEvent} logout={logout} />
        </div>
        {isTaskRouterPage && (
          <TaskRouterHeaderInfo
            handleAgentAvailability={handleAgentAvailability}
          />
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
